import * as angular from 'angular';
import type { RoutingService } from '../App/services/mx-routing-service';
import { DashboardPageComponent } from './dashboard-page/dashboard-page.component';
import { DashboardWidgetComponent } from './dashboard-widget/dashboard-widget.component';
import { DashboardService } from './dashboard.service';
import type { Transition } from '@uirouter/angularjs';
import { WidgetConfigurationPanelComponent } from './widget-configuration-panel/widget-configuration-panel.component';
import { DashboardControlPanelComponent } from './dashboard-control-panel/dashboard-control-panel.component';
import { WidgetCategoriesPanelComponent } from './widget-categories-panel/widget-categories-panel.component';
import { DashboardConfigurationPanelComponent } from './dashboard-configuration-panel/dashboard-configuration-panel.component';
import { WidgetInstanceActionsComponent } from './widget-instance-actions/widget-instance-actions.component';
import { DashboardLocalizationService } from './dashboard-localization.service';
import { DashboardFiltersService } from './dashboard-filters.service';
import { DashboardFiltersComponent } from './dashboard-interactive-filters/dashboard-filters-panel.component';
import { DashboardBooleanFilterComponent } from './dashboard-interactive-filters/boolean-filter/dashboard-boolean-filter.component';
import { DashboardDateFilterComponent } from './dashboard-interactive-filters/date-filter/dashboard-date-filter.component';
import { DashboardObjectsFilterComponent } from './dashboard-interactive-filters/objects-filter/dashboard-objects-filter.component';
import { DashboardEnumFilterComponent } from './dashboard-interactive-filters/enum-filter/dashboard-enum-filter.component';
import { DashboardExportPreviewComponent } from './dashboard-export-preview/dashboard-export-preview.component';
import { DashboardReportComponent } from './dashboard-report/dashboard-report.component';
import { DashboardStringFilterComponent } from './dashboard-interactive-filters/string-filter/dashboard-string-filter.component';
import { DashboardNumberFilterComponent } from './dashboard-interactive-filters/number-filter/dashboard-number-filter.component';
import { TableComponent } from './mx-table/mx-table.component';
import { GeoMapComponent } from './mx-geo-map/mx-geo-map.component';
import { InteractiveFiltersConfigPanelComponent } from './interactive-filters-config-panel/interactive-filters-config-panel.component';
import { DashboardFilterComponent } from './dashboard-interactive-filters/filter/dashboard-filter.component';
import { DashboardPersonalizationService } from './dashboard-personalization.service';
import lodash from '../Utils/mx-lodash';

export const dashboardRouteName = 'dashboard';

export const dashboardModule = angular
	.module('mx.sb.Dashboard', [])
	.service('mx.sb.Dashboard.PersonalizationService', DashboardPersonalizationService)
	.service('mx.sb.Dashboard.Service', DashboardService)
	.service('mx.sb.Dashboard.DashboardLocalizationService', DashboardLocalizationService)
	.service('mx.sb.Dashboard.DashboardFiltersService', DashboardFiltersService)
	.component(DashboardPageComponent.name, DashboardPageComponent)
	.component(DashboardWidgetComponent.name, DashboardWidgetComponent)
	.component(WidgetConfigurationPanelComponent.name, WidgetConfigurationPanelComponent)
	.component(DashboardControlPanelComponent.name, DashboardControlPanelComponent)
	.component(WidgetCategoriesPanelComponent.name, WidgetCategoriesPanelComponent)
	.component(DashboardConfigurationPanelComponent.name, DashboardConfigurationPanelComponent)
	.component(WidgetInstanceActionsComponent.name, WidgetInstanceActionsComponent)
	.component(DashboardFiltersComponent.name, DashboardFiltersComponent)
	.component(DashboardFilterComponent.name, DashboardFilterComponent)
	.component(DashboardBooleanFilterComponent.name, DashboardBooleanFilterComponent)
	.component(DashboardDateFilterComponent.name, DashboardDateFilterComponent)
	.component(DashboardObjectsFilterComponent.name, DashboardObjectsFilterComponent)
	.component(DashboardEnumFilterComponent.name, DashboardEnumFilterComponent)
	.component(DashboardStringFilterComponent.name, DashboardStringFilterComponent)
	.component(DashboardNumberFilterComponent.name, DashboardNumberFilterComponent)
	.component(DashboardExportPreviewComponent.name, DashboardExportPreviewComponent)
	.component(DashboardReportComponent.name, DashboardReportComponent)
	.component(TableComponent.name, TableComponent)
	.component(GeoMapComponent.name, GeoMapComponent)
	.component(InteractiveFiltersConfigPanelComponent.name, InteractiveFiltersConfigPanelComponent)
	.run([
		'mx.shell.RoutingService',
		function (routingService: RoutingService) {
			routingService.addItems([
				routingService.createRoutingItem({
					name: dashboardRouteName,
					path: '/dashboard/:navigationId?:filters',
					component: DashboardPageComponent.name,
					params: {
						navigationId: '',
						filters: {
							array: false,
							dynamic: true,
						},
					},
					resolve: [
						{
							token: 'dashboardLoader',
							policy: { when: 'EAGER', async: 'NOWAIT' },
							deps: ['$transition$'],
							resolveFn: async ($transition$: Transition) => {
								const dashboardService: DashboardService = $transition$.injector().get('mx.sb.Dashboard.Service');
								const dashboardFiltersService: DashboardFiltersService = $transition$.injector().get('mx.sb.Dashboard.DashboardFiltersService');
								const dashboardPersonalizationService: DashboardPersonalizationService = $transition$.injector().get('mx.sb.Dashboard.PersonalizationService');
								const initialFilters = dashboardFiltersService.parseFiltersRoute($transition$.params().filters ?? '');
								const navigationId = $transition$.params().navigationId.toLowerCase();

								const hasUrlFilters = !lodash.isEmpty(initialFilters);

								let personalizedFilters = undefined;

								if (!hasUrlFilters) {
									personalizedFilters = await dashboardPersonalizationService.getFilterPersonalizationAsync(navigationId);
								}

								const updatedFilters = hasUrlFilters ? initialFilters : personalizedFilters;

								const dashboard = await dashboardService.getDashboard(navigationId, true, updatedFilters);

								if (!hasUrlFilters && personalizedFilters) {
									dashboardService.navigateToDashboard(dashboard);
								}

								return dashboard;
							},
						},
					],
				}),
			]);
		},
	]);
